'use client';

import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { Box, Grid, Stack, Typography } from '@/primitives';
import { blue, slate } from '@/lib/colours';
import type { LandingFeatureIconKey } from './homeLandingContent';
import { landingFeatureSection, landingFeatures } from './homeLandingContent';
import { FadeInWhenVisible } from './FadeInWhenVisible';
import { mutedSectionBg, sectionShell } from './homeLandingSectionSx';

const iconMap: Record<LandingFeatureIconKey, typeof SearchOutlinedIcon> = {
  search: SearchOutlinedIcon,
  people: PeopleOutlineOutlinedIcon,
  inventory: Inventory2OutlinedIcon,
  storefront: StorefrontOutlinedIcon,
  assignment: AssignmentTurnedInOutlinedIcon,
  biotech: BiotechOutlinedIcon,
  science: ScienceOutlinedIcon,
  verified: VerifiedUserOutlinedIcon,
};

export function LandingFeatureGrid() {
  return (
    <Box
      component="section"
      aria-labelledby="landing-features-title"
      sx={{
        py: { xs: 8, md: 10 },
        ...mutedSectionBg,
      }}
    >
      <Box sx={sectionShell}>
        <FadeInWhenVisible>
          <Stack
            alignItems="center"
            spacing={1}
            sx={{ mb: { xs: 4, md: 6 }, textAlign: 'center', width: '100%' }}
          >
            <Typography
              color={blue[700]}
              sx={{
                fontWeight: 600,
                letterSpacing: '0.08em',
                fontSize: '0.75rem',
                textAlign: 'center',
              }}
              variant="labelmedium"
            >
              {landingFeatureSection.eyebrow}
            </Typography>
            <Typography
              color={slate[900]}
              component="h2"
              id="landing-features-title"
              sx={{ maxWidth: 720, mx: 'auto', textAlign: 'center' }}
              variant="headlinelarge"
            >
              {landingFeatureSection.title}
            </Typography>
            <Typography
              color={slate[600]}
              sx={{ maxWidth: 560, mx: 'auto', mt: 1, textAlign: 'center' }}
              variant="bodylarge"
            >
              {landingFeatureSection.subtitle}
            </Typography>
          </Stack>
        </FadeInWhenVisible>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {landingFeatures.map((item, index) => {
            const Icon = iconMap[item.icon];
            return (
              <Grid
                key={item.title}
                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                sx={{ display: 'flex' }}
              >
                <FadeInWhenVisible delay={index * 0.05} fillParent>
                  <Stack
                    sx={{
                      flex: 1,
                      width: '100%',
                      minHeight: 0,
                      p: 2.5,
                      borderRadius: '14px',
                      border: `1px solid ${slate[200]}`,
                      backgroundColor: slate[50],
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        borderColor: blue[200],
                        boxShadow: `0 16px 48px -28px ${blue[600]}55`,
                      },
                    }}
                    spacing={2}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        flexShrink: 0,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: blue[50],
                        color: blue[700],
                      }}
                    >
                      <Icon sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography
                      color={slate[900]}
                      sx={{ flexShrink: 0 }}
                      variant="titlemedium"
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      color={slate[600]}
                      sx={{ flexGrow: 1 }}
                      variant="bodymedium"
                    >
                      {item.description}
                    </Typography>
                  </Stack>
                </FadeInWhenVisible>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
}
